import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
    ILoanCharacteristicsData,
    ILoanCharacteristicsParams,
} from '../interfaces/pipeline-characteristics-data';
import { ICardInfo } from 'app/modules/my-limegear/components/standalone-components/interfaces/stanalone-components.data';
import { NoDataActionType } from 'app/modules/shared/interfaces/no-data';
import { IChartTitleData } from 'app/modules/shared/interfaces/card-data';
import * as Highcharts from 'highcharts';
import { Router } from '@angular/router';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import { DecimalPipe } from '@angular/common';
import { DisplayOptions } from 'app/modules/shared/interfaces/display-options';
import { SeriesOptionsType } from 'highcharts';

@Injectable({
    providedIn: 'root',
})
export class PipelineLoanCharacteristicsService {
    private serverUrl = 'https://' + window.location.host; // TODO Get url and version number from appsettings.
    private version = '0.5';
    private _url = (this.serverUrl + '/api/v{version}/LoanCharacteristics/').replace(
        '{version}',
        this.version
    );

    colorMap: Map<string, string> = new Map([
        ['conv', '#B870FF'],
        ['govt', '#57D785'],
        ['other', '#FF9494'],
        ['reverse', '#2C76A2'],
        ['non-conf', '#FFDB70'],
        ['refinance', '#669999'],
        ['construction', '#9FA278'],
        ['purchase', '#FF9966'],
    ]);

    constructor(
        private _http: HttpClient,
        private _router: Router,
        private reusableFunctions: ReusableFunctions,
        private _decimalPipe: DecimalPipe
    ) {}

    getCurrentPipelineCharacteristics(
        parameters: ILoanCharacteristicsParams
    ): Observable<ILoanCharacteristicsData[]> {
        return this._http.post<ILoanCharacteristicsData[]>(
            this._url + 'GetCurrentPipelineCharacteristics',
            parameters,
            {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            }
        );
    }

    setDataParameters(
        displayOptions: DisplayOptions[],
        hFilterJSON: string,
        type: string,
        module = ''
    ) {
        let params: ILoanCharacteristicsParams = {
            channel: '',
            loanType: '',
            purpose: '',
            lockStatus: '',
            milestone: '',
            rollUpLockStatus: '',
            displayAs: '',
            hFilterJSON: hFilterJSON,
            type: type,
            module: module,
            fromDatePeriod: '',
        };

        params.channel =
            displayOptions?.find((item) => item.optionKey === 'channel')?.selectedOptions ?? 'All';

        params.loanType =
            displayOptions?.find((item) => item.optionKey === 'loanCategory')?.selectedOptions ??
            'All';

        params.purpose =
            displayOptions?.find((item) => item.optionKey === 'loanPurpose')?.selectedOptions ??
            'All';

        params.lockStatus =
            displayOptions?.find((item) => item.optionKey === 'loanLockStatus')?.selectedOptions ??
            'All';

        params.displayAs =
            displayOptions?.find((item) => item.optionKey === 'displayAs')?.selectedOptions ??
            'Volume';

        params.milestone =
            displayOptions?.find((item) => item.optionKey === 'activeMilestones')
                ?.selectedOptions ?? 'All';

        params.fromDatePeriod =
            displayOptions?.find((item) => item.optionKey === 'dateRange_y_q_m')?.selectedOptions ??
            '';
        return params;
    }

    getLoanCharacteristics(
        chartCardInfo: ICardInfo,
        displayOptions: DisplayOptions[],
        hFilterJSON: string,
        action: NoDataActionType,
        rollUpLockStatus: string,
        filterType: string,
        bandType = '',
        chartType = 'pie',
        altFilterType = null
    ) {
        let params = this.setDataParameters(
            displayOptions,
            hFilterJSON,
            filterType,
            chartCardInfo.module
        );
        this.updateLargeCardInteriorOptions(chartCardInfo, false, false, action);
        params.rollUpLockStatus = rollUpLockStatus;

        this.getCurrentPipelineCharacteristics(params).subscribe(
            (data) => {
                let hasData = data && data.length > 0;
                if (hasData) {
                    let navFromUrl = this._router.url.split('?')[0];
                    let chartData: any[];
                    let chartOptions: Highcharts.Options = {};
                    let cardInteriorOptions = chartCardInfo.largeCardInteriorOptions;
                    let queryParams = this.setUrlParams(
                        filterType,
                        chartCardInfo.name,
                        params,
                        navFromUrl
                    );

                    if (chartType === 'pie') {
                        chartData = this.setDonutChartData(
                            data,
                            chartCardInfo.name,
                            filterType,
                            params,
                            altFilterType
                        );
                        chartOptions = this.getDonutChartOptions(
                            chartData,
                            cardInteriorOptions.accessToUrl,
                            navFromUrl
                        );
                    } else {
                        chartData = this.setStackChartData(
                            data,
                            chartCardInfo.name,
                            params,
                            bandType,
                            filterType
                        );
                        chartOptions = this.getStackCartOptions(
                            chartData,
                            cardInteriorOptions.accessToUrl,
                            params,
                            navFromUrl
                        );
                    }
                    cardInteriorOptions.queryParams = queryParams;
                    cardInteriorOptions.showCardControlArea = true;
                    cardInteriorOptions.chart = new Highcharts.Chart(
                        chartCardInfo.cardId,
                        chartOptions
                    );
                    this.updateLargeCardInteriorOptions(chartCardInfo, hasData, true, action);
                } else {
                    this.updateLargeCardInteriorOptions(chartCardInfo, false, true, action);
                }
            },
            (error) => {
                this.updateLargeCardInteriorOptions(
                    chartCardInfo,
                    false,
                    true,
                    NoDataActionType.error
                );
                throw new Error(error);
            }
        );
    }

    updateLargeCardInteriorOptions(
        chartCardInfo: ICardInfo,
        hasData: boolean,
        isDataReady: boolean,
        action: NoDataActionType
    ) {
        let cardInteriorOptions = chartCardInfo.largeCardInteriorOptions;
        cardInteriorOptions.hasData = hasData;
        cardInteriorOptions.isDataReady = isDataReady;
        cardInteriorOptions.action = action;
        chartCardInfo.largeCardInteriorOptions = Object.assign({}, cardInteriorOptions);
    }

    setUrlParams(
        filterType: string,
        title: string,
        params: ILoanCharacteristicsParams,
        navFromUrl: string
    ) {
        return {
            milestone: params.milestone,
            loanType: params.loanType,
            filterType: filterType,
            filterValue: 'All',
            navFromUrl: navFromUrl,
            titleSuffix: `${title}: All`,
        };
    }

    setDonutChartData(
        data: ILoanCharacteristicsData[],
        title: string,
        filterType: string,
        params: ILoanCharacteristicsParams,
        altFilterType: string
    ) {
        return data.map((i) => {
            let value: any;
            if (params.displayAs === 'Volume') {
                value = i.loanVolume;
            } else {
                value = i.loanUnits;
            }

            let point: string = i[filterType];
            let color: string;
            if (this.colorMap.has(point.toLowerCase())) {
                color = this.colorMap.get(point.toLowerCase());
            }

            return {
                name: point,
                color: color,
                y: value,
                properties: {
                    units: i.loanUnits,
                    volume: i.loanVolume,
                    filterType: altFilterType ?? filterType,
                    filterValue: point,
                    titleSuffix: `${title}: ${point}`,
                },
            };
        });
    }

    showDescriptionText() {
        let url = this._router.url.split('?')[0];
        return !(
            url === '/pipeline/insights/loan-characteristics' ||
            url === '/pipeline/insights/borrower-characteristics' ||
            url === '/production/insights/funded-loan-characteristics' ||
            url === '/production/insights/funded-borrower-characteristics'
        );
    }

    initializeLargeCard(
        largeCardInfo: ICardInfo,
        action: NoDataActionType,
        cardDrilldownUrl: string,
        minHeight = 300
    ) {
        let chartTitleData: IChartTitleData = {
            showChartCustomTitle: true,
            chartTile: largeCardInfo.name,
            chartLabelColor: '#dededed',
            chartComparisonValue: null,
            chartMainValue: '',
            chartSecondaryValue: '',
            isMainValueUnitVisible: false,
            chartSubItemValue: null,
            isChartSubValueVisible: false,
            svgIconString: '',
            showMainValue: false,
            comparisonStyle: null,
            isDonut: true,
            descriptionText: largeCardInfo.module ?? '',
            showDescriptionText: this.showDescriptionText(),
        };

        let largeCardInteriorOptions = {
            action: action,
            componentTitle: largeCardInfo.name,
            searchTerm: '',
            hasData: false,
            isDataReady: false,
            showTitle: true,
            accessToUrl: cardDrilldownUrl + '/' + largeCardInfo.urlSegment,
            uniqueId: largeCardInfo.cardId,
            showCardControllers: true,
            queryParams: {},
            minHeight: minHeight,
            chartTitleData: chartTitleData,
        };
        largeCardInfo.largeCardInteriorOptions = Object.assign({}, largeCardInteriorOptions);
    }

    getDonutChartOptions(seriesData, routerUrl: string, navFromUrl: string): Highcharts.Options {
        const _this = this;

        return {
            chart: {
                height: 290,
                type: 'pie',
                marginBottom: 96,
            },
            exporting: {
                enabled: false,
            },
            title: {
                text: null,
                align: 'left',
                x: 14,
                style: {
                    fontFamily: 'Roboto',
                    fontSize: '23px',
                    color: '#223540',
                },
            },
            series: [
                {
                    type: 'pie',
                    showInLegend: false,
                    data: seriesData,
                } as Highcharts.SeriesPieOptions,
            ],
            subtitle: null,
            credits: {
                enabled: false,
            },
            tooltip: {
                style: {
                    color: '#223540',
                    fontFamily: 'Roboto',
                    fontSize: '20',
                    height: 12,
                },
                borderColor: '#223540',
                borderRadius: 6,
                backgroundColor: '#FFFFFF',
                useHTML: true,
                shared: true,
                followPointer: true,
                distance: 15,
                formatter: function () {
                    let toolTip = '';
                    try {
                        let totalUnits = 0;
                        let totalVolume = 0;
                        let unitsPercentage = '';
                        let volumePercentage = '';
                        this.point.series.points.forEach((p) => {
                            totalUnits += p.properties.units;
                            totalVolume += p.properties.volume;
                        });

                        unitsPercentage = _this.reusableFunctions.formatNumber(
                            (100 * this.point.properties.units) / totalUnits,
                            1,
                            'percentage'
                        );
                        volumePercentage = _this.reusableFunctions.formatNumber(
                            (100 * this.point.properties.volume) / totalVolume,
                            1,
                            'percentage'
                        );
                        toolTip = `<span style="color: ${
                            this.point.color
                        }; font-weight: bold; text-align: right; padding-right: 5px;">${
                            this.point.name
                        }</span>  <br> \                           
                            <span style="text-align: right;">${_this.reusableFunctions.formatMoney(
                                this.point.properties.volume
                            )}</span> \
                            <span style="text-align: right;"><b> (${volumePercentage})</b></span>\
                            <span style="padding: 0 1px 0 1px">/</span> \
                            <span style="text-align: right;">${_this.reusableFunctions.formatNumber(
                                this.point.properties.units,
                                0,
                                'number'
                            )}</span> \
                            <span style="text-align: right;"><b> (${unitsPercentage})</b> units</span>`;
                    } catch (e) {
                        console.log(e);
                    }
                    return toolTip;
                },
            },
            plotOptions: {
                pie: {
                    point: {
                        events: {
                            click: function (pointEvent) {
                                let params = pointEvent.point.properties;
                                _this._router.navigate([routerUrl], {
                                    queryParams: {
                                        filterType: params.filterType ?? '',
                                        filterValue: params.filterValue ?? '',
                                        navFromUrl: navFromUrl ?? '',
                                        titleSuffix: params.titleSuffix ?? '',
                                    },
                                });
                            },
                        },
                    },
                    innerSize: '80%',
                    center: ['50%', '50%'],
                    size: 150,
                    dataLabels: {
                        enabled: true,
                        connectorShape: 'crookedLine',
                        alignTo: 'plotEdges',
                        crookDistance: 90,
                        format: '<span style="font-weight: normal; white-space: nowrap;">{point.name} {point.percentage:.1f} %</span>',
                    },
                },
            },
        };
    }

    setStackChartData(
        data: ILoanCharacteristicsData[],
        title: string,
        params: ILoanCharacteristicsParams,
        bandType: string,
        filterType: string
    ): SeriesOptionsType[] {
        let categories = new Set(data.map((item) => item[bandType]));
        let indexMap: Map<string, number> = new Map();
        let index: number = 0;
        categories.forEach((category) => {
            indexMap.set(category, index);
            index++;
        });

        var stack: { [loanType: string]: Array<any> } = {};
        stack = data.reduce(
            (a, x) => ({
                ...a,
                [x.loanType]: data.filter((y) => y.loanType == x.loanType),
            }),
            {}
        );

        let series: SeriesOptionsType[] = [];
        Object.keys(stack).forEach((loanType) => {
            let typeSeries = {} as SeriesOptionsType;
            let pointData: any[] = [];
            let typeArray: any[] = stack[loanType];
            for (let type of typeArray) {
                let value: number;
                if (params.displayAs == 'Volume') {
                    value = type.loanVolume;
                } else {
                    value = type.loanUnits;
                }

                let point = {
                    y: value,
                    name: type[bandType],
                    properties: {
                        units: type.loanUnits,
                        volume: type.loanVolume,
                        filterType: filterType,
                        filterValue: type[bandType],
                        loanType: loanType,
                        titleSuffix: `${title}: ${type[bandType]}`,
                        labelPoint: true,
                    },
                };
                pointData.push(point);
            }
            typeSeries['data'] = pointData;
            typeSeries['name'] = loanType;
            typeSeries['color'] = this.colorMap.get(loanType.toLowerCase());
            categories.forEach((category) => {
                if (!typeSeries['data'].find((obj) => obj.name == category)) {
                    typeSeries['data'].splice(indexMap.get(category), 0, {
                        y: null,
                        name: category,
                    });
                }
            });

            series.push(typeSeries);
        });
        return series.reverse();
    }

    getStackCartOptions(
        seriesData,
        routerUrl: string,
        params,
        navFromUrl: string
    ): Highcharts.Options {
        const _this = this;
        return {
            chart: {
                type: 'bar',
                //height: '400',
                spacingLeft: 15,
                spacingRight: 15,
                spacingTop: 5,
                scrollablePlotArea: {},
                events: {
                    render: function () {
                        var xAxis = this.xAxis[0];
                        var xTicks = xAxis.ticks;
                        let isInialLoadCompleted = false;
                        if (!isInialLoadCompleted) {
                            let tickKeys = Object.keys(xTicks);
                            let points: Highcharts.Point[] = [];
                            this.series.forEach((series) => {
                                points = points.concat(series.points);
                            });

                            try {
                                tickKeys.forEach((key, i) => {
                                    let xTick = xTicks[key];
                                    if (xTick && xTick.label) {
                                        let pointInf = points.find((point) => {
                                            return (
                                                point.name &&
                                                xTick.label.element.textContent &&
                                                point.name.toLowerCase().trim() ===
                                                    xTick.label.element.textContent
                                                        .toLowerCase()
                                                        .trim() &&
                                                point.properties?.labelPoint
                                            );
                                        });

                                        if (pointInf) {
                                            let label = xTick.label.element;
                                            label.style.cursor = 'pointer';
                                            label.onclick = function (item) {
                                                let queryParams = {
                                                    loanType: 'All',
                                                    filterValue: pointInf.name,
                                                    filterType: pointInf.properties.filterType,
                                                    titleSuffix: pointInf.properties.titleSuffix,
                                                    navFromUrl: navFromUrl,
                                                };
                                                _this._router.navigate([routerUrl], {
                                                    queryParams: queryParams,
                                                });
                                            };
                                        }
                                    }
                                    isInialLoadCompleted = true;
                                });
                            } catch (error) {
                                throw new Error(error);
                            }
                        }
                    },
                },
            },
            xAxis: {
                type: 'category',
                title: {
                    text: null,
                },
                labels: {
                    step: 1,
                    style: {
                        fontSize: '13px',
                        color: '#333333',
                        padding: '6',
                        fontWeight: 'bold',
                    },
                },
                plotLines: [],
            },
            title: {
                text: '',
            },
            yAxis: {
                title: {
                    text: params.displayAs === 'Volume' ? 'Volume ($)' : 'Units',
                    style: {
                        fontSize: '10px',
                        color: '#666666',
                    },
                },

                labels: {
                    enabled: true,
                    style: {
                        fontSize: '10px',
                        color: '#666666',
                    },
                },
                lineWidth: 2,
                plotLines: [],
            },
            legend: {
                enabled: true,
                align: 'left',
                verticalAlign: 'top',
                reversed: true,
            },
            plotOptions: {
                series: {
                    point: {},
                },
                bar: {
                    maxPointWidth: 50,
                    stacking: 'normal',
                    states: {
                        hover: {
                            brightness: -0.3,
                        },
                    },
                    point: {
                        events: {
                            click: function (pointEvent) {
                                let pointInfo = pointEvent.point.properties;
                                _this._router.navigate([routerUrl], {
                                    queryParams: {
                                        loanType: pointInfo.loanType,
                                        filterType: pointInfo.filterType,
                                        filterValue: pointInfo.filterValue,
                                        navFromUrl: navFromUrl,
                                        titleSuffix:
                                            pointInfo.titleSuffix + ' ' + pointInfo.loanType,
                                    },
                                });
                            },
                        },
                    },
                },
            },
            tooltip: {
                style: {
                    color: '#223540',
                    fontFamily: 'Roboto',
                    fontSize: '12',
                    height: 12,
                },
                borderColor: '#223540',
                borderRadius: 6,
                backgroundColor: '#FFFFFF',
                useHTML: true,
                shared: true,
                followPointer: true,
                distance: 15,
                formatter: function () {
                    let tooltip = '';
                    let length = this.points.length;
                    let thisPeriodTotal = 0;
                    let thisPeriodUnitTotal = 0;

                    tooltip =
                        '<table style="border-collapse: collapse">' +
                        this.points.reduce(function (s, point, index) {
                            let toolText = '';
                            let value = _this.reusableFunctions.formatMoney(
                                point.point.properties.volume
                            );
                            let seriesName = point.series.name;
                            if (index === 0) {
                                s =
                                    s +
                                    '<td colSpan="4" style="text-align: left;"><b>' +
                                    point.point.name.toUpperCase() +
                                    '<b></td></tr>';
                            }

                            thisPeriodUnitTotal += point.point.properties.units;
                            thisPeriodTotal += point.point.properties.volume;
                            toolText =
                                s +
                                '<tr style="line-height: 16px;"><td class="tooltip-data-title" ' +
                                'style="color: ' +
                                point.point.color +
                                '"><b>' +
                                seriesName +
                                '</b></td><td style="text-align: right"> ' +
                                value +
                                '</td>' +
                                '<td style="padding: 0 5px 0 5px">/</td><td style="text-align: right;">' +
                                point.point.properties.units?.toLocaleString() +
                                '</td>' +
                                '</tr>';
                            if (index === length - 1) {
                                toolText =
                                    toolText +
                                    '<tr style="border-top: 1px solid #b3b3b3; line-height: 16px;"><td><b>Total ' +
                                    '</b></td><td style="text-align: right;"><b>' +
                                    _this.reusableFunctions.formatMoney(thisPeriodTotal) +
                                    '</b></td>' +
                                    '<td style="padding: 0 5px 0 5px">/</td><td style="text-align: right;"><b>' +
                                    thisPeriodUnitTotal.toLocaleString() +
                                    '</b></td>' +
                                    '</tr>';
                            }

                            return toolText;
                        }, '<tr style="line-height: 18px; font-size: 14px">') +
                        '</table>';

                    return tooltip;
                },
            } as Highcharts.TooltipOptions,
            credits: {
                enabled: false,
            },
            series: seriesData,
            exporting: {
                enabled: false,
            },
        };
    }
}
